import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { faArrowLeftLong, faPaperPlane, faTimes } from "@fortawesome/pro-light-svg-icons";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@features/authenticate/services/auth.service";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { CapsLockNoticeComponent } from "@shared/components/caps-lock-notice/caps-lock-notice.component";
import { HeadlineTitleComponent } from "../../ui/headline/components/title/headline-title.component";
import { HeadlineDescriptionComponent } from "../../ui/headline/components/description/headline-description.component";
import { NotifyChipComponent } from "@shared/components/notify-chip/notify-chip.component";
import { NotifyChipValueComponent } from "@shared/components/notify-chip/components/notify-chip-value/notify-chip-value.components";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { HeadlineComponent } from "../../ui/headline/headline.component";
import { CORE_ROUTES } from "@core/constants/routes.constant";
import { AUTHENTICATE_ROUTES } from "@features/authenticate/constants/routes.constant";
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-authenticate-page-forgot-password",
  templateUrl: "./forgot-password.component.html",
  styleUrl: "forgot-password.component.scss",
  imports: [ IconModule,
    ReactiveFormsModule,
    CapsLockNoticeComponent,
    HeadlineTitleComponent,
    HeadlineDescriptionComponent,
    NotifyChipComponent,
    NotifyChipValueComponent,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    HeadlineComponent,
    TranslationPipeModule ],
  standalone: true,
  providers: [ provideTranslationScope("feature/auth/forgot-password") ]
})
export class ForgotPasswordComponent {
  private readonly faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private _authService: AuthService = inject(AuthService);

  protected isLoading: WritableSignal<boolean> = signal(false);

  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  private readonly _fallbackBackwardTarget: string = `${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.LOGIN }`;

  private readonly _router: Router = inject(Router);

  public isEmailSent: WritableSignal<boolean> = signal(false);

  protected readonly formGroup: FormGroup<{
    email: FormControl<string | null>;
  }> = new FormGroup<{
      email: FormControl<string | null>;
    }>({
      /* eslint-disable-next-line @tseslint/unbound-method */
      email: new FormControl("", [ Validators.required, Validators.email ])
    });

  constructor () {
    this.faIconLibrary.addIcons(faTimes, faArrowLeftLong, faPaperPlane);
  }

  protected handleBackward (): void {
    const referrerParam: string | null = this._activatedRoute.snapshot.queryParamMap.get("referrer");
    const referrer: string = referrerParam === null
      ? this._fallbackBackwardTarget
      : decodeURI(referrerParam);

    void this._router.navigateByUrl(referrer);
  }

  protected handleClear (): void {
    this.formGroup.get("email")?.setValue("");
  }


  protected handleSubmit (): void {
    const email: string | null | undefined = this.formGroup.get("email")?.value;

    if (email) {
      this.isLoading.set(true);
      this._authService.sendPasswordResetMail(email).subscribe(() => {
        this.isEmailSent.set(true);
        this.isLoading.set(false);
      });
    }
  }
}
